'use client'

import Image from 'next/image'
import Link from 'next/link'
import { type CSSProperties, useEffect, useRef } from 'react'

import { getCategoryUrl } from '@/features/categories/constants/routes'
import type { Subcategory } from '@/features/categories/types'
import { Button } from '@/shared/components/ui/Button'
import { buildCdnUrl } from '@/shared/constants/publicUrlConstants'
import { useCategoriesBarStore } from '@/store/categoriesBarStore'
import {
  CATEGORIES_CARD_SKELETON_KEYS,
  CATEGORIES_DESKTOP_OUTLINE_RADIUS,
  CATEGORIES_DESKTOP_SKELETON_TAB_KEYS,
  CATEGORIES_DESKTOP_TAB_HEIGHT,
  CATEGORIES_MOBILE_OUTLINE_RADIUS,
  CATEGORIES_MOBILE_SKELETON_TAB_KEYS,
  CATEGORIES_MOBILE_TAB_HEIGHT,
} from '../constants/uiConstants'
import { useCategoriesSection } from '../hooks/useCategoriesSection'
import { buildMobileTabLabel } from '../util/normalizeText'
import { buildOutlinePath } from '../util/outlinePath'

const CATEGORIES_FADE_KEYFRAMES = `@keyframes categoriesCardFade {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}`

const CATEGORIES_CARD_FADE_STYLE: CSSProperties = {
  animation: 'categoriesCardFade 300ms ease-out',
}

// Ease-out (decelerate): starts moving immediately on click instead of the
// slow ramp-up of an ease-in-out curve, which read as a delay.
const CATEGORIES_TAB_EASE = 'cubic-bezier(0.22, 0.61, 0.36, 1)'
const CATEGORIES_LINE_TRANSITION = `d 240ms ${CATEGORIES_TAB_EASE}`

function SubcategoryTitle({ name, className }: { name: string; className: string }) {
  const splitIdx = name.indexOf(' and ')
  if (splitIdx === -1) {
    return <p className={className}>{name}</p>
  }
  return (
    <p className={className}>
      {name.slice(0, splitIdx + 4)}
      <br />
      {name.slice(splitIdx + 5)}
    </p>
  )
}

function subcategoryImageSrc(sub: Subcategory, mobile: boolean): string | null {
  const path = mobile
    ? sub.mobileImageUrl || sub.imageUrl
    : sub.imageUrl || sub.mobileImageUrl
  if (!path) {
    return null
  }
  return buildCdnUrl(path)
}

const Categories = () => {
  const {
    categories,
    loading,
    selectedIndex,
    setSelectedIndex,
    subcategories,
    desktopSectionRef,
    desktopTabRefs,
    desktopGeometry,
    mobileSectionRef,
    mobileTabRefs,
    mobileGeometry,
  } = useCategoriesSection()

  const activeCategory = categories[selectedIndex]

  const subcategoryHref = (sub: Subcategory) =>
    activeCategory
      ? `${getCategoryUrl(activeCategory.slug)}?sub_category=${encodeURIComponent(sub.slug)}`
      : getCategoryUrl(sub.slug)

  const desktopVisibilityRef = useRef<HTMLElement>(null)
  const setHomeCategoriesPassed = useCategoriesBarStore(
    (state) => state.setHomeCategoriesPassed,
  )

  useEffect(() => {
    const el = desktopVisibilityRef.current
    if (!el) {
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        // "Passed" = the section has scrolled up and out, above the sticky header.
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0
        setHomeCategoriesPassed(passed)
      },
      { threshold: 0, rootMargin: '-96px 0px 0px 0px' },
    )

    observer.observe(el)
    return () => {
      observer.disconnect()
      setHomeCategoriesPassed(false)
    }
  }, [setHomeCategoriesPassed])

  if (!loading && categories.length === 0) {
    return null
  }

  const desktopOutlinePath = buildOutlinePath(
    desktopGeometry.width,
    CATEGORIES_DESKTOP_TAB_HEIGHT,
    CATEGORIES_DESKTOP_OUTLINE_RADIUS,
    desktopGeometry.activeLeft,
    desktopGeometry.activeWidth,
  )
  const mobileOutlinePath = buildOutlinePath(
    mobileGeometry.width,
    CATEGORIES_MOBILE_TAB_HEIGHT,
    CATEGORIES_MOBILE_OUTLINE_RADIUS,
    mobileGeometry.activeLeft,
    mobileGeometry.activeWidth,
    // Bleed the baseline under the scroll container's px-[14px] so the active
    // tab's underline reaches the section's left/right edge.
    14,
  )

  return (
    <>
      <style>{CATEGORIES_FADE_KEYFRAMES}</style>
      <section className="bg-white md:hidden">
        <div
          className={loading ? 'relative border-b-[6px] border-[#001F5F]' : 'relative'}
        >
          <div
            className={
              loading
                ? 'overflow-x-auto px-[14px] pt-px translate-y-[2px] scrollbar-hide'
                : 'overflow-x-auto px-[14px] pt-px pb-[2px] scrollbar-hide'
            }
          >
            <div
              ref={mobileSectionRef}
              className={
                loading
                  ? 'flex min-w-max items-end gap-6 text-center text-[12px] font-bold leading-4 text-[#001F5F]'
                  : 'relative flex h-[30px] min-w-max items-end gap-6 text-center text-[12px] font-bold leading-4 text-[#001F5F]'
              }
            >
              {!loading && mobileGeometry.width > 0 && (
                <svg
                  aria-hidden="true"
                  className="pointer-events-none absolute bottom-0 left-0 z-0 overflow-visible"
                  style={{
                    width: mobileGeometry.width,
                    height: CATEGORIES_MOBILE_TAB_HEIGHT,
                  }}
                  viewBox={`0 0 ${mobileGeometry.width} ${CATEGORIES_MOBILE_TAB_HEIGHT}`}
                  fill="none"
                >
                  <defs>
                    <linearGradient id="categoriesMobileFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#D4DEEE" />
                      <stop offset="100%" stopColor="#FFFFFF" />
                    </linearGradient>
                  </defs>
                  <path
                    d={mobileOutlinePath}
                    fill="url(#categoriesMobileFill)"
                    stroke="#001F5F"
                    strokeWidth="3"
                    style={{ transition: CATEGORIES_LINE_TRANSITION }}
                  />
                </svg>
              )}
              {loading &&
                CATEGORIES_MOBILE_SKELETON_TAB_KEYS.map((key, index) => (
                  <div
                    key={key}
                    className={
                      index === 0
                        ? 'flex h-[30px] min-w-[94px] shrink-0 items-center justify-center rounded-t-[15px] border-2 border-b-0 border-[#001F5F] bg-gradient-to-b from-[#D4DEEE] to-white'
                        : 'h-[30px] min-w-[64px] shrink-0 animate-pulse rounded-md bg-neutral-100'
                    }
                  />
                ))}
              {!loading &&
                categories.map((category, index) => (
                  <Button
                    key={category.id}
                    ref={(el: HTMLButtonElement | null) => {
                      mobileTabRefs.current[index] = el
                    }}
                    type="button"
                    onClick={() => setSelectedIndex(index)}
                    variant="ghost"
                    className={
                      index === selectedIndex
                        ? 'relative z-10 flex h-[30px] min-w-[74px] shrink-0 items-center justify-center overflow-hidden rounded-t-[15px] px-2'
                        : 'h-auto shrink-0 px-0 pb-[7px] pt-0 hover:bg-transparent'
                    }
                  >
                    {buildMobileTabLabel(category.name)}
                  </Button>
                ))}
            </div>
          </div>
        </div>

        <div
          key={selectedIndex}
          style={loading ? undefined : CATEGORIES_CARD_FADE_STYLE}
          className="flex gap-3 overflow-x-auto px-[14px] py-[9px] scrollbar-hide"
        >
          {loading &&
            CATEGORIES_CARD_SKELETON_KEYS.map((key) => (
              <div
                key={key}
                className="flex w-[83px] shrink-0 flex-col items-center gap-[5px]"
              >
                <div className="h-[84px] w-[84px] animate-pulse rounded-[15px] bg-neutral-100" />
                <div className="h-3 w-14 animate-pulse rounded bg-neutral-100" />
              </div>
            ))}
          {!loading &&
            subcategories.map((sub) => {
              const img = subcategoryImageSrc(sub, true)
              if (!img) {
                return null
              }

              return (
                <Link
                  key={sub.id}
                  href={subcategoryHref(sub)}
                  className="flex w-[83px] shrink-0 flex-col items-center gap-[5px]"
                >
                  <Image
                    src={img}
                    alt=""
                    width={84}
                    height={84}
                    sizes="84px"
                    quality={60}
                    className="h-[84px] w-[84px] rounded-[15px] object-cover"
                  />
                  <SubcategoryTitle
                    name={sub.name}
                    className="text-center text-[12px] leading-[13px] text-[#001F5F]"
                  />
                </Link>
              )
            })}
        </div>
      </section>

      <section ref={desktopVisibilityRef} className="hidden w-full bg-white md:block">
        <div className="relative pt-[13px]">
          <div
            ref={desktopSectionRef}
            className={
              loading
                ? 'relative min-h-[29px] border-b-[6px] border-[#001F5F]'
                : 'relative'
            }
          >
            {!loading && desktopGeometry.width > 0 && (
              <svg
                aria-hidden="true"
                className="pointer-events-none absolute bottom-0 left-0 z-0 overflow-visible"
                style={{
                  width: desktopGeometry.width,
                  height: CATEGORIES_DESKTOP_TAB_HEIGHT,
                }}
                viewBox={`0 0 ${desktopGeometry.width} ${CATEGORIES_DESKTOP_TAB_HEIGHT}`}
                fill="none"
              >
                <defs>
                  <linearGradient id="categoriesDesktopFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#808FAF" />
                    <stop offset="60%" stopColor="#FFFFFF" />
                    <stop offset="100%" stopColor="#FFFFFF" />
                  </linearGradient>
                </defs>
                <path
                  d={desktopOutlinePath}
                  fill="url(#categoriesDesktopFill)"
                  stroke="#001F5F"
                  strokeWidth="3"
                  style={{ transition: CATEGORIES_LINE_TRANSITION }}
                />
              </svg>
            )}
            <div className="mx-auto max-w-[1440px]">
              <div
                className={
                  loading
                    ? 'flex min-h-[29px] items-end gap-[30px] overflow-x-auto pl-[24px] pr-[61px] translate-y-[2px] scrollbar-hide'
                    : 'flex h-[42px] items-end gap-[30px] overflow-x-auto pl-[24px] pr-[61px] scrollbar-hide'
                }
              >
                {loading &&
                  CATEGORIES_DESKTOP_SKELETON_TAB_KEYS.map((key, index) => (
                    <div
                      key={key}
                      className={
                        index === 0
                          ? 'h-[42px] w-[217px] shrink-0 animate-pulse rounded-t-[15px] border-2 border-b-0 border-[#001F5F] bg-neutral-50'
                          : 'mb-[5px] h-6 w-[160px] shrink-0 animate-pulse rounded bg-neutral-100'
                      }
                    />
                  ))}
                {!loading &&
                  categories.map((category, index) => (
                    <Button
                      key={category.id}
                      ref={(el: HTMLButtonElement | null) => {
                        desktopTabRefs.current[index] = el
                      }}
                      type="button"
                      onClick={() => setSelectedIndex(index)}
                      variant="ghost"
                      className={
                        index === selectedIndex
                          ? 'relative z-10 flex h-[42px] min-w-[217px] shrink-0 items-center justify-center overflow-hidden rounded-t-[15px] px-[27px] text-center text-[24px] font-semibold leading-[24px] text-[#001F5F]'
                          : 'h-auto shrink-0 px-0 pb-[5px] pt-0 text-center text-[24px] font-semibold leading-[24px] text-[#001F5F] hover:bg-transparent hover:text-[#001F5F]/80'
                      }
                    >
                      {category.name}
                    </Button>
                  ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-[1440px]">
          <div
            key={selectedIndex}
            style={loading ? undefined : CATEGORIES_CARD_FADE_STYLE}
            className="flex h-[212px] items-center gap-[42px] overflow-x-auto px-[64px] scrollbar-hide"
          >
            {loading &&
              CATEGORIES_CARD_SKELETON_KEYS.map((key) => (
                <div
                  key={key}
                  className="flex w-[136px] shrink-0 flex-col items-center gap-[5px]"
                >
                  <div className="h-[136px] w-[136px] animate-pulse rounded-[15px] bg-neutral-100" />
                  <div className="h-4 w-24 animate-pulse rounded bg-neutral-100" />
                </div>
              ))}
            {!loading &&
              subcategories.map((sub) => {
                const img = subcategoryImageSrc(sub, false)
                if (!img) {
                  return null
                }

                return (
                  <Link
                    key={sub.id}
                    href={subcategoryHref(sub)}
                    className="flex w-[136px] shrink-0 flex-col items-center gap-[5px]"
                  >
                    <Image
                      src={img}
                      alt=""
                      width={136}
                      height={136}
                      sizes="136px"
                      quality={60}
                      className="h-[136px] w-[136px] rounded-[15px] object-cover"
                    />
                    <SubcategoryTitle
                      name={sub.name}
                      className="text-center text-[16px] leading-[18px] text-[#001F5F]"
                    />
                  </Link>
                )
              })}
          </div>
        </div>
      </section>
    </>
  )
}

export default Categories
